/**
 * 散点图组件
 *
 * 使用 ECharts 实现的散点图，用于展示满意度与提及次数的关系
 */

import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { ScatterChart as EChartsScatterChart, ScatterSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { ScatterDataItem } from '@/types/analysis';
import { useThemeStore } from '@/stores/themeStore';
import { getCommonEChartsTheme } from '@/utils/chartTheme';
import './ScatterChart.css';

echarts.use([
  EChartsScatterChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  VisualMapComponent,
  CanvasRenderer,
]);

interface ScatterChartProps {
  data: ScatterDataItem[];
  height?: number;
  title?: string;
  loading?: boolean;
  xAxisName?: string;
  yAxisName?: string;
}

export function ScatterChart({
  data,
  height = 400,
  title,
  loading = false,
  xAxisName = '满意度',
  yAxisName = '提及次数'
}: ScatterChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const currentTheme = useThemeStore((s) => s.theme);

  useEffect(() => {
    if (!chartRef.current || loading) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }

    const chart = chartInstanceRef.current;

    // 转换数据格式为散点图所需格式
    const scatterData = data.map((item) => [item.x, item.y, item.label || '']);

    const commonTheme = getCommonEChartsTheme(currentTheme);
    const titleColor = currentTheme === 'dark' ? '#FFFFFF' : '#1E293B';
    const nameTextColor = currentTheme === 'dark' ? '#9CA3AF' : '#5C5C5C';
    const emphasisBorder = currentTheme === 'dark' ? '#FFFFFF' : '#333';

    // 使用 CSS 变量读取功能色
    const root = document.documentElement;
    const cs = getComputedStyle(root);
    const errorColor = cs.getPropertyValue('--color-error').trim() || '#ff4d4f';
    const warningColor = cs.getPropertyValue('--color-warning').trim() || '#faad14';
    const successColor = cs.getPropertyValue('--color-success').trim() || '#52c41a';

    const option: echarts.EChartsCoreOption = {
      title: {
        text: title,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
          color: titleColor,
        },
      },
      tooltip: {
        ...commonTheme.tooltip,
        trigger: 'item',
        formatter: (params: any) => {
          const [x, y, label] = params.value;
          return `${label || '未知'}<br/>${xAxisName}: <strong>${x}</strong><br/>${yAxisName}: <strong>${y}</strong>`;
        },
      },
      grid: {
        ...commonTheme.grid,
        left: '10%',
        right: '10%',
        bottom: '15%',
        top: title ? '20%' : '15%',
      },
      xAxis: {
        name: xAxisName,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          color: nameTextColor,
          fontSize: 12,
          fontWeight: 600,
        },
        type: 'value',
        ...commonTheme.xAxis,
        min: 0,
        max: 10,
      },
      yAxis: {
        name: yAxisName,
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: {
          color: nameTextColor,
          fontSize: 12,
          fontWeight: 600,
        },
        type: 'value',
        ...commonTheme.yAxis,
      },
      visualMap: {
        show: false,
        dimension: 0,
        min: 0,
        max: 10,
        inRange: {
          color: [errorColor, warningColor, successColor],
        },
      },
      series: [
        {
          type: 'scatter',
          symbolSize: (val: number[]) => {
            // 根据 y 值（提及次数）调整散点大小
            return Math.max(8, Math.min(30, val[1] / 2));
          },
          data: scatterData,
          emphasis: {
            focus: 'self',
            itemStyle: {
              borderColor: emphasisBorder,
              borderWidth: 2,
            },
          },
          itemStyle: {
            opacity: 0.8,
          },
        } as ScatterSeriesOption,
      ],
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, title, loading, xAxisName, yAxisName, currentTheme]);

  useEffect(() => {
    return () => {
      chartInstanceRef.current?.dispose();
    };
  }, []);

  if (loading) {
    return (
      <div className="scatter-chart-loading" style={{ height }}>
        <div className="loading-spinner">加载中...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="scatter-chart-empty" style={{ height }}>
        <div className="empty-message">暂无数据</div>
      </div>
    );
  }

  return <div ref={chartRef} className="scatter-chart" style={{ height }} />;
}
